import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { resolveAthletePhoto, resolveAthleteBanner } from "@/lib/photoFallback";
import { useOgTags } from "@/lib/ogTags";
import { cleanJourneyForDisplay } from "@/lib/journeyText";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ValidationShield, ConfidenceDot } from "@/components/ValidationShield";
import { UnclaimedAthleteBanner } from "@/components/UnclaimedAthleteBanner";
import { Crown, FileDown, Heart, Share2, MapPin, Building2, ShieldCheck, ExternalLink, Pencil, Sparkles, Camera, Radio, X, AlertTriangle, Search as SearchIcon, Calendar, Trophy, Newspaper, BookOpen, ListChecks, Play, Image as ImageIcon, ArrowUpRight, Plus, Trash2, Facebook, Instagram, Link2, Flag, Loader2, CheckCircle2 } from "lucide-react";

// V11.15 — Icône TikTok (non fournie par lucide-react)
function TiktokIcon({ className = "h-4 w-4" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.5 8.6a6.5 6.5 0 0 1-4.9-2.1v9.8a5.2 5.2 0 1 1-5.2-5.2c.3 0 .5 0 .8.1v2.7c-.3-.1-.5-.1-.8-.1a2.5 2.5 0 1 0 2.5 2.5V3h2.7A6.6 6.6 0 0 0 19.5 5.9z" />
    </svg>
  );
}
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { PublicEntityLinks } from "@/components/EntityLinks";
import { EntityRelationsWidget } from "@/components/EntityRelationsWidget";
import { EntityBadge } from "@/components/EntityBadge";
import { EntityTimeline } from "@/components/EntityTimeline";
import { ShareButton } from "@/components/ShareButton";
import { CoachPanel } from "@/components/CoachPanel";
import { AthleteClaimCTA } from "@/components/AthleteClaimCTA";
import { AthleteCampaignBanner } from "@/components/AthleteCampaignBanner";
import { SupportCampaignBanner } from "@/components/SupportCampaignBanner";
import { SocialPostEmbed, detectSocialPlatform } from "@/components/SocialPostEmbed";
import { showError } from "@/lib/errors";

/* ---- Sidebar entity row with thumbnail lookup ----------------------------
 * Affiche une liste d'entités (coachs/clubs/fédés) avec miniatures résolues
 * côté serveur via un appel UNIQUE `/api/athletes/{id}/affiliations-resolved`
 * (au lieu de N appels parallèles depuis le client).
 * Si la prop `resolved` est fournie (= déjà chargée par le parent), pas de fetch.
 */
// KIND_DETAIL retiré (V9.13) — remplacé par EntityBadge unifié.

function SidebarEntityList({ icon: Icon, iconColor, label, names, kind, resolved }) {
  const items = useMemo(() => {
    if (Array.isArray(resolved) && resolved.length > 0) return resolved;
    // Fallback : pas encore résolu — affiche les noms bruts (sans miniature)
    return (names || []).map((n) => ({ name: n, slug: null, image: null, found: false, link_status: null }));
  }, [names, resolved]);

  return (
    <div className="flex items-start gap-2 text-sm" data-testid={`sidebar-list-${kind}`}>
      <Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: iconColor }} />
      <div className="min-w-0 flex-1">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <ul className="space-y-1.5">
          {items.map((r) => (
            <li key={r.name}>
              {/* V9.13 — EntityBadge unifié : coche = link_status="accepted" uniquement. */}
              <EntityBadge
                kind={kind}
                name={r.name}
                slug={r.slug}
                image={r.image}
                link_status={r.link_status}
                size="sm"
                variant="row"
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


// ---- helpers --------------------------------------------------------------
const PROFILE_TYPE_LABEL = {
  actif: "Athlète actif",
  ancien_athlete: "Ancien athlète",
  en_pause: "Athlète en pause",
  reconverti: "Athlète reconverti",
  athlete_professionnel: "Athlète professionnel",
  athlete_amateur: "Athlète amateur",
  competiteur_local: "Compétiteur local",
  competiteur_territorial: "Compétiteur territorial",
  competiteur_national: "Compétiteur national",
  competiteur_regional: "Compétiteur régional",
  competiteur_international: "Compétiteur international",
  coach: "Coach",
  dirigeant_sportif: "Dirigeant sportif",
  benevole_sportif: "Bénévole sportif",
  // Legacy — même rendu que les canoniques modernes (mapping neutralisé)
  coach_ancien_competiteur: "Coach",
  dirigeant_ancien_athlete: "Dirigeant sportif",
  benevole_ancien_competiteur: "Bénévole sportif",
  sportif_amateur_parcours: "Sportif amateur — parcours documenté",
  sportif_amateur_confirme: "Sportif amateur confirmé",
  parcours_a_completer: "Parcours sportif à compléter",
  non_specifie: "Parcours sportif",
};

const STATUS_LABEL = {
  haut_niveau: "Haut niveau confirmé",
  potentiel_haut_niveau: "Potentiel haut niveau",
  parcours_documente: "Parcours sportif documenté",
};

const MEDAL_EMOJI = { or: "🥇", argent: "🥈", bronze: "🥉", finaliste: "🏅" };

function medalForAchievement(a) {
  if (a.medal && MEDAL_EMOJI[a.medal]) return MEDAL_EMOJI[a.medal];
  const t = `${a.title || ""} ${a.level || ""}`.toLowerCase();
  if (/champion(?!nat)|or\b|gold|m[ée]daille d['’]or/.test(t)) return "🥇";
  if (/argent|silver|vice-champion|2e\b/.test(t)) return "🥈";
  if (/bronze|3e\b/.test(t)) return "🥉";
  return "🏆";
}

function groupAchievementsByYearDiscipline(achievements) {
  const byYear = {};
  for (const a of achievements || []) {
    const y = a.year || "Année inconnue";
    const d = a.discipline || "Sans discipline";
    byYear[y] = byYear[y] || {};
    byYear[y][d] = byYear[y][d] || [];
    byYear[y][d].push(a);
  }
  // V9.12 — Tri intra-année par mois descendant (les plus récents en haut).
  // Sans mois renseigné, on garde l'ordre d'insertion (= ordre du tableau).
  const monthOf = (a) => (a && a.month && Number.isFinite(+a.month) ? +a.month : 0);
  for (const y of Object.keys(byYear)) {
    for (const d of Object.keys(byYear[y])) {
      byYear[y][d] = [...byYear[y][d]].sort((x, z) => monthOf(z) - monthOf(x));
    }
  }
  const sortedYears = Object.keys(byYear).sort((a, b) => {
    if (a === "Année inconnue") return 1;
    if (b === "Année inconnue") return -1;
    return Number(b) - Number(a);
  });
  return { byYear, sortedYears };
}

// ---- Sub-components -------------------------------------------------------
function ProfileBadges({ athlete }) {
  // Fiche à compléter : aucun palmarès et aucun article référencé.
  const needsContent = (athlete.achievements || []).length === 0 && (athlete.articles || []).length === 0;
  return (
    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-white/80">
      {/* V13.7 — Badge majeur JP 2027 pour un athlète sélectionné officiellement */}
      {athlete.road_to_2027 && (
        <Badge
          className="bg-[hsl(var(--aito-gold))] text-black border border-black/10 font-semibold"
          title="Sélectionné(e) officiellement pour les Jeux du Pacifique 2027"
          data-testid="badge-jp2027-selected"
        >
          <Flag className="h-3 w-3 mr-1" />
          Sélectionné(e) JP 2027
          {athlete.road_to_2027_discipline && <span className="ml-1 opacity-90">· {athlete.road_to_2027_discipline}</span>}
        </Badge>
      )}
      <Badge className="bg-white/15 text-white border border-white/20">{PROFILE_TYPE_LABEL[athlete.profile_type || "non_specifie"]}</Badge>
      {athlete.also_offers_coaching && (
        <Badge
          className="bg-[hsl(var(--aito-lagoon))] text-black border border-black/10"
          title="Cette personne se déclare aussi coach — sélectionnable comme coach depuis d'autres fiches."
          data-testid="badge-also-coach"
        >
          Aussi coach
        </Badge>
      )}
      {athlete.main_discipline && <Badge variant="outline" className="border-white/30 text-white">{athlete.main_discipline}</Badge>}
      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{athlete.commune || athlete.island || "Polynésie"}</span>
      <ValidationShield level={athlete.validation?.level || "declaratif"} />
      <ConfidenceDot score={athlete.confidence} />
      {/* V13.7 — Badges validation COPF / Fédération / Incohérence signalée */}
      <ProfileValidationBadge athlete={athlete} />
      {needsContent && (
        <Badge
          variant="outline"
          className="border-dashed border-[hsl(var(--aito-gold))]/60 text-[hsl(var(--aito-gold))] bg-transparent"
          title="Cette fiche n'a pas encore de palmarès ni d'articles. Le propriétaire peut les ajouter à tout moment."
          data-testid="badge-fiche-a-completer"
        >Fiche à compléter</Badge>
      )}
    </div>
  );
}

// V13.7 — Badge de validation officielle (COPF / Fédération) avec état incohérence.
function ProfileValidationBadge({ athlete }) {
  // V13.11 — Badges indépendants COPF / Fédération.
  //   - N'affiche RIEN tant que la validation n'a pas été effective côté validateur.
  //   - Fallback rétro-compat : si seuls les champs legacy `profile_validation_*`
  //     sont présents (fiches validées avant V13.9), on les mappe sur le bon rôle.
  const copfOk = !!athlete.copf_palmares_validated_at
    || (athlete.profile_validation_status === "validated" && athlete.profile_validation_by_role === "copf");
  const fedOk = !!athlete.federation_palmares_validated_at
    || (athlete.profile_validation_status === "validated" && athlete.profile_validation_by_role === "federation");
  const fedName = athlete.federation_palmares_federation_name
    || athlete.profile_validation_federation_name;
  const status = athlete.profile_validation_status || "none";
  // V13.20 — Une incohérence signalée invalide TOUTE validation précédente
  // (défensif côté UI, en complément du reset backend).
  const inconsistency = status === "inconsistency";
  const showCopfBadge = copfOk && !inconsistency;
  const showFedBadge = fedOk && !inconsistency;

  if (!showCopfBadge && !showFedBadge && !inconsistency) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="profile-validation-badges">
      {showCopfBadge && (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm border border-white/20"
          style={{ background: "linear-gradient(135deg, #059669 0%, #047857 100%)", boxShadow: "0 2px 10px rgba(5,150,105,.4)" }}
          title="Palmarès validé par le Comité Olympique Polynésien Français"
          data-testid="badge-profile-validated-copf"
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          <span className="uppercase tracking-wider text-[10px]">Palmarès validé · </span>
          <span>COPF</span>
        </span>
      )}
      {showFedBadge && (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-lg backdrop-blur-sm border border-white/20"
          style={{ background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)", boxShadow: "0 2px 10px rgba(13,148,136,.4)" }}
          title={`Palmarès validé par ${fedName || "la Fédération"}`}
          data-testid="badge-profile-validated-federation"
        >
          <ShieldCheck className="h-3.5 w-3.5" />
          <span className="uppercase tracking-wider text-[10px]">Palmarès validé · </span>
          <span>{fedName || "Fédération"}</span>
        </span>
      )}
      {inconsistency && (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white shadow-md border border-white/20"
          style={{ background: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)" }}
          title={athlete.profile_validation_notes ? `Incohérence : ${athlete.profile_validation_notes}` : "Incohérence signalée"}
          data-testid="badge-profile-inconsistency"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Incohérence signalée</span>
        </span>
      )}
    </div>
  );
}

function FlashRecapCard({ athlete }) {
  // V13.6 — Récap éclair recentré : plus de compteurs sportifs (déjà présents
  // dans "Chiffres clés"). On garde uniquement le contexte structurant et on
  // ajoute le badge officiel "Haut niveau" quand le statut est validé.
  const r = athlete.flash_recap || {};
  const NO_DATA = "Données à compléter";
  const _list = (arr) => Array.isArray(arr) && arr.length ? arr.join(", ") : null;
  const _periode = (athlete.period_start || r.period_start)
    ? `${athlete.period_start || r.period_start} – ${athlete.period_end ?? r.period_end ?? "présent"}`
    : null;
  const mediaCount = (athlete.articles || []).length + (athlete.sources || []).length;

  const rawItems = [
    ["Type de profil", PROFILE_TYPE_LABEL[athlete.profile_type || r.profile_type || ""] || athlete.profile_type || null],
    ["Discipline principale", athlete.main_discipline || r.main_discipline],
    ["Disciplines secondaires", _list((athlete.disciplines || []).filter((d) => d !== (athlete.main_discipline || r.main_discipline)))],
    ["Période de pratique", _periode],
    ["Niveau sportif", r.max_level_reached || athlete.sport_level],
    ["Sources & médias disponibles", mediaCount > 0 ? `${mediaCount} référence${mediaCount > 1 ? "s" : ""}` : null],
  ];
  const ESSENTIAL = new Set([
    "Type de profil", "Discipline principale", "Période de pratique", "Niveau sportif",
  ]);
  const items = rawItems.filter(([k, v]) => v !== null && v !== undefined && v !== "" ? true : ESSENTIAL.has(k))
    .map(([k, v]) => [k, (v !== null && v !== undefined && v !== "") ? v : NO_DATA]);

  const showHighLevelBadge = !!athlete.is_high_level_validated;
  const showHighLevelClaimed = !showHighLevelBadge && !!athlete.is_high_level_claimed;

  if (items.length === 0 && !showHighLevelBadge && !showHighLevelClaimed && !athlete.road_to_2027) return null;
  return (
    <Card className="p-5" data-testid="flash-recap-card">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Sparkles className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <h2 className="font-display font-semibold text-lg">Récap éclair</h2>
        {athlete.road_to_2027 && (
          <Badge
            data-testid="badge-jp2027-recap"
            className="ml-auto bg-[hsl(var(--aito-gold))] text-black shrink-0 font-semibold"
            title="Sélection officielle Jeux du Pacifique 2027"
          >
            <Flag className="h-3 w-3 mr-1" />
            JP 2027 · Sélectionné(e)
          </Badge>
        )}
        {showHighLevelBadge && (
          <Badge
            data-testid="badge-high-level-validated"
            className={`${athlete.road_to_2027 ? "" : "ml-auto"} bg-[hsl(var(--aito-gold))] text-black shrink-0`}
          >
            ★ Haut niveau confirmé
            {athlete.high_level_validated_by_role ? ` · ${athlete.high_level_validated_by_role.toUpperCase()}` : ""}
          </Badge>
        )}
        {showHighLevelClaimed && (
          <Badge
            data-testid="badge-high-level-claimed"
            variant="outline"
            className={`${athlete.road_to_2027 ? "" : "ml-auto"} shrink-0 text-[hsl(var(--aito-gold))] border-[hsl(var(--aito-gold))]`}
          >
            ★ Haut niveau — validation en cours
          </Badge>
        )}
      </div>
      <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map(([k, v]) => {
          const isMissing = v === "Données à compléter";
          return (
            <div key={k} className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2 py-1 border-b border-dashed last:border-b-0 min-w-0">
              <dt className="text-muted-foreground sm:min-w-[140px] sm:shrink-0 break-words">{k}</dt>
              <dd className={"sm:text-right sm:ml-auto break-words min-w-0 " + (isMissing ? "text-muted-foreground italic" : "font-medium")}>{String(v)}</dd>
            </div>
          );
        })}
      </dl>
      {r.confidence_note && (
        <p className="mt-3 text-xs text-muted-foreground italic">{r.confidence_note}</p>
      )}
    </Card>
  );
}

function HighlightsCard({ athlete }) {
  const items = athlete.highlights || [];
  if (items.length === 0) return null;
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
        <h2 className="font-display font-semibold text-lg">Faits marquants</h2>
      </div>
      <ul className="space-y-1.5">
        {items.map((h, i) => (
          <li key={i} className="text-sm flex items-start gap-2"><span className="text-[hsl(var(--aito-gold))]">•</span><span>{h}</span></li>
        ))}
      </ul>
    </Card>
  );
}

function DisciplineStatsTable({ athlete, disciplines }) {
  // V13.6 — Simplification radicale : par discipline, uniquement 3 compteurs
  // (Résultats / Podiums / Articles). Plus de "commentaire" ni de niveau proposé.
  const stats = athlete.discipline_stats || [];
  if (stats.length === 0) return null;
  const disciplineName = (slug) => disciplines.find((d) => d.slug === slug)?.name || slug;
  return (
    <Card className="p-5" data-testid="discipline-stats-card">
      <div className="flex items-center gap-2 mb-3">
        <ListChecks className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <h2 className="font-display font-semibold text-lg">Statistiques par discipline</h2>
      </div>
      <div className="space-y-3">
        {stats.map((s, i) => {
          const nbResults = s.results ?? s.competitions ?? s.matches ?? 0;
          const nbPodiums = s.podiums ?? 0;
          const nbArticles = s.articles_found ?? 0;
          return (
            <div key={i} className="border rounded-lg p-3">
              <div className="font-display font-semibold text-sm">
                {disciplineName(s.discipline_slug) || s.discipline_name || s.discipline_slug}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div className="bg-secondary/50 rounded px-2 py-1.5" data-testid={`stat-${s.discipline_slug}-results`}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Résultats</div>
                  <div className="font-mono font-semibold text-base">{nbResults}</div>
                </div>
                <div className="bg-secondary/50 rounded px-2 py-1.5" data-testid={`stat-${s.discipline_slug}-podiums`}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Podiums</div>
                  <div className="font-mono font-semibold text-base">{nbPodiums}</div>
                </div>
                <div className="bg-secondary/50 rounded px-2 py-1.5" data-testid={`stat-${s.discipline_slug}-articles`}>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Articles</div>
                  <div className="font-mono font-semibold text-base">{nbArticles}</div>
                </div>
              </div>
              {(s.sources || []).length > 0 && (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  Sources : {s.sources.join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function JourneyCard({ athlete }) {
  // V13.6 cleanup — on supprime à l'affichage les marqueurs techniques
  // injectés par la migration (base inchangée).
  const cleaned = cleanJourneyForDisplay(athlete.journey_text);
  if (!cleaned) return null;
  return (
    <Card className="p-5" data-testid="journey-card">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <h2 className="font-display font-semibold text-lg">Parcours sportif</h2>
      </div>
      <p className="text-[15px] leading-relaxed whitespace-pre-line">{cleaned}</p>
    </Card>
  );
}

function formatMonthYear(a) {
  // V9.12 — Formate "mars 2024" si mois + année saisis, sinon juste "2024".
  if (!a || !a.year) return null;
  const m = a.month && Number.isFinite(+a.month) ? +a.month : null;
  if (!m || m < 1 || m > 12) return String(a.year);
  const NAMES = ["janvier","février","mars","avril","mai","juin","juillet","août","septembre","octobre","novembre","décembre"];
  return `${NAMES[m - 1]} ${a.year}`;
}

function AchievementCard({ a, athlete }) {
  // V11.6 — Meta adaptée à la famille sportive
  const family = a.sport_family;
  const baseMeta = [a.competition && a.competition !== a.title ? a.competition : null, a.place].filter(Boolean);
  const familyMeta = [];
  if (family === "combat") {
    if (a.weight) familyMeta.push(a.weight);
    if (a.age_class) familyMeta.push(a.age_class);
    if (a.belt) familyMeta.push(`🥋 ${a.belt}`);
    if (a.opponent) familyMeta.push(`vs ${a.opponent}`);
    if (a.match_outcome) familyMeta.push(a.match_outcome);
  } else if (family === "team") {
    if (a.team_name) familyMeta.push(`👥 ${a.team_name}`);
    if (a.category) familyMeta.push(a.category);
  } else if (family === "timed") {
    if (a.distance) familyMeta.push(a.distance);
    if (a.time_performance) familyMeta.push(`⏱ ${a.time_performance}`);
    if (a.category) familyMeta.push(a.category);
  } else if (family === "vaa") {
    if (a.crew_format) familyMeta.push(a.crew_format);
    if (a.distance) familyMeta.push(a.distance);
    if (a.team_name) familyMeta.push(`🌊 ${a.team_name}`);
    if (a.time_performance) familyMeta.push(`⏱ ${a.time_performance}`);
  } else if (family === "strength") {
    if (a.weight) familyMeta.push(a.weight);
    if (a.category) familyMeta.push(a.category);
  } else if (family === "racket") {
    if (a.team_name) familyMeta.push(`👥 ${a.team_name}`);
    if (a.category) familyMeta.push(a.category);
  } else {
    // legacy fallback
    if (a.age_class) familyMeta.push(a.age_class);
    if (a.belt) familyMeta.push(a.belt);
    if (a.weight) familyMeta.push(a.weight);
    if (a.category) familyMeta.push(a.category);
    if (a.club) familyMeta.push(a.club);
  }
  const meta = [...baseMeta, ...familyMeta];
  const ranks = [a.level, a.medal ? `médaille ${a.medal}` : (a.rank ? `${a.rank}e` : null)].filter(Boolean);
  const when = formatMonthYear(a);
  const canShare = !!a.id && !!athlete?.slug;
  const medalEmoji = medalForAchievement(a);
  // V11.2 — Statut visible pour COPF/Admin (Déclaré / À vérifier / Validé / Rejeté)
  // Source canonique : Achievement.result_type + Achievement.validation_status si présent.
  const rawStatus = (a.validation_status || a.result_type || "declare").toLowerCase();
  const statusMap = {
    valide:        { label: "Validé",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    valid:         { label: "Validé",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    validated:     { label: "Validé",     cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    officiel:      { label: "Officiel",   cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    a_confirmer:   { label: "À vérifier", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    a_verifier:    { label: "À vérifier", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    pending:       { label: "À vérifier", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    declare:       { label: "Déclaré",    cls: "bg-slate-100 text-slate-700 border-slate-300" },
    declared:      { label: "Déclaré",    cls: "bg-slate-100 text-slate-700 border-slate-300" },
    rejected:      { label: "Rejeté",     cls: "bg-red-100 text-red-800 border-red-300" },
    invalide:      { label: "Rejeté",     cls: "bg-red-100 text-red-800 border-red-300" },
  };
  const st = statusMap[rawStatus] || statusMap.declare;
  return (
    <Card
      id={a.id ? `achievement-${a.id}` : undefined}
      className={`p-3 relative bg-background/95 backdrop-blur-sm shadow-sm ${
        a.is_major_title
          ? "border-[hsl(var(--aito-gold))]/50 bg-[hsl(var(--aito-gold))]/10"
          : ""
      }`}
      data-testid="achievement-card"
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl shrink-0 mt-0.5" aria-hidden>{medalEmoji}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="font-display font-semibold text-sm leading-tight break-words min-w-0 flex-1">
              {a.title}
              {a.is_collective && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] uppercase font-normal tracking-wider text-muted-foreground border rounded px-1 py-0.5" data-testid="achievement-collective-badge">
                  👥 collectif
                </span>
              )}
            </div>
            {/* V13.11 — Badges empilés à droite (Titre majeur puis Statut) pour éviter tout empiètement. */}
            <div className="flex flex-col items-end gap-1 shrink-0">
              {a.is_major_title && (
                <Badge
                  className="text-[9px] font-bold uppercase tracking-wider py-0 px-1.5 bg-[hsl(var(--aito-gold))] text-black border-transparent whitespace-nowrap"
                  title="Titre majeur"
                  data-testid="achievement-major-badge"
                >
                  <Crown className="h-3 w-3 mr-0.5" />Titre majeur
                </Badge>
              )}
              {/* V13.19 — Badge Jeux du Pacifique (résultat issu d'un import historique officiel) */}
              {a.games_result_id && (
                <Badge
                  className="text-[9px] font-bold uppercase tracking-wider py-0 px-1.5 bg-[hsl(var(--aito-lagoon))]/15 text-[hsl(var(--aito-lagoon))] border border-[hsl(var(--aito-lagoon))]/30 whitespace-nowrap"
                  title="Résultat officiel importé depuis les archives des Jeux du Pacifique"
                  data-testid="achievement-games-badge"
                >
                  <Trophy className="h-3 w-3 mr-0.5" />Jeux du Pacifique
                </Badge>
              )}
              <Badge variant="outline" className={`text-[10px] py-0 whitespace-nowrap ${st.cls}`} data-testid={`achievement-status-${st.label.toLowerCase()}`}>{st.label}</Badge>
            </div>
          </div>
          {when && <div className="text-[11px] text-[hsl(var(--aito-lagoon))] font-mono mt-0.5">{when}</div>}
          {meta.length > 0 && <div className="text-xs text-muted-foreground mt-0.5 break-words">{meta.join(" · ")}</div>}
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {ranks.map((r) => <Badge key={r} variant="secondary" className="text-[10px] py-0">{r}</Badge>)}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {a.source_url && (
              <a href={a.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--aito-lagoon))] hover:underline">
                <ExternalLink className="h-3 w-3" />Voir la source
              </a>
            )}
            {canShare && (
              <ShareButton
                kind="achievement"
                slug={a.id}
                title={`${medalEmoji} ${a.title} — ${athlete.full_name}`}
                text={[a.competition, a.discipline, a.year && String(a.year)].filter(Boolean).join(" · ")}
                canonicalPath={`/athletes/${athlete.slug}#achievement-${a.id}`}
                dataTestId={`achievement-share-${a.id}`}
                variant="ghost"
                size="sm"
              />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// OverviewSummary — Récapitulatif valorisant et sobre du parcours sportif
// ---------------------------------------------------------------------------

// Disciplines de combat (compteur "Combats")
const COMBAT_DISCIPLINES = new Set([
  "boxe", "boxe-anglaise", "boxe-thai", "muay-thai", "mma", "kickboxing",
  "jiu-jitsu", "jiu-jitsu-bresilien", "bjj", "judo", "karate", "taekwondo",
  "lutte", "savate", "krav-maga", "grappling",
]);
// Sports collectifs (compteur "Matchs")
const TEAM_DISCIPLINES = new Set([
  "football", "rugby", "basket", "basketball", "volley", "volley-ball",
  "handball", "water-polo", "hockey", "baseball", "softball", "rugby-a-xv",
  "rugby-a-7", "futsal",
]);

function deriveCompetitionLabel(athlete) {
  // Choisit le libellé adapté au sport principal de l'athlète.
  const slugs = [athlete.main_discipline, ...(athlete.disciplines || [])].filter(Boolean).map((s) => String(s).toLowerCase());
  for (const s of slugs) {
    if (COMBAT_DISCIPLINES.has(s)) return { label: "Combats enregistrés", singular: "combat" };
    if (TEAM_DISCIPLINES.has(s)) return { label: "Matchs enregistrés", singular: "match" };
  }
  return { label: "Compétitions enregistrées", singular: "compétition" };
}

function StatTile({ label, value, sublabel, testid }) {
  return (
    <div className="rounded-lg border bg-card p-3 text-center" data-testid={testid}>
      <div className="text-2xl font-display font-bold text-[hsl(var(--aito-gold))]">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground mt-0.5">{label}</div>
      {sublabel && <div className="text-[10px] text-muted-foreground/80 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function PeopleBlock({ title, items, icon: Icon, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
        <Icon className="h-3.5 w-3.5" />
        <span>{title}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, i) => {
          const isCoach = title.toLowerCase().includes("coach");
          const label = typeof it === "string" ? it : (it.name || it.title);
          const meta = typeof it === "string" ? null : (it.club || it.discipline);
          return (
            <Badge key={i} variant="secondary" className="text-xs font-normal py-1 pl-2 pr-2.5">
              <span>{label}</span>
              {meta && <span className="text-muted-foreground ml-1.5">· {meta}</span>}
              {isCoach && <span className="ml-2 text-[9px] uppercase tracking-wider text-muted-foreground border-l border-border pl-1.5">Déclaré · validation à venir</span>}
            </Badge>
          );
        })}
      </div>
    </div>
  );
}

function OverviewSummary({ athlete, disciplines, visibleAchievements, visibleArticles, isOwner, palmaresMode, setPalmaresMode }) {
  // V13.6 — Structure simplifiée : plus de "Environnement sportif" en doublon
  // avec la colonne de droite, plus de "Faits marquants" ni "Bio longue".
  //   1) Chiffres clés  →  Médailles / Médailles OR / Résultats enregistrés
  //   2) Titres majeurs → SYNCHRONISÉ sur `is_major_title` du palmarès (fallback : top 5 pondérés)
  //   3) Palmarès & Parcours → intégrés ici même, sous les titres majeurs
  //   4) Statistiques par discipline (chiffres bruts, sans commentaire)

  // Compteurs canoniques
  const medalsCount = visibleAchievements.filter((a) => ["or", "argent", "bronze"].includes((a.medal || "").toLowerCase())).length;
  const goldCount   = visibleAchievements.filter((a) => (a.medal || "").toLowerCase() === "or").length;
  const resultsCount = (athlete.results || []).length + visibleAchievements.length;

  // Titres majeurs — SOURCE UNIQUE : la case `is_major_title` cochée dans le palmarès.
  // Fallback : si aucun titre majeur explicite, top 5 par pondération médaille/niveau/année.
  const explicitMajors = visibleAchievements.filter((a) => a.is_major_title === true);
  const medalWeight = { or: 4, argent: 3, bronze: 2, finaliste: 1 };
  const levelWeight = { olympique: 5, mondial: 4, international: 3, continental: 3, oceanien: 2, national: 2, territorial: 1 };
  const fallbackTop = [...visibleAchievements]
    .sort((a, b) =>
      (medalWeight[(b.medal || "").toLowerCase()] || 0) - (medalWeight[(a.medal || "").toLowerCase()] || 0)
      || (levelWeight[(b.level || "").toLowerCase()] || 0) - (levelWeight[(a.level || "").toLowerCase()] || 0)
      || (b.year || 0) - (a.year || 0)
    )
    .slice(0, 5);
  const topAchievements = explicitMajors.length > 0 ? explicitMajors : fallbackTop;

  const hasAnyData = visibleAchievements.length > 0 || (athlete.coaches || []).length > 0 || athlete.journey_text;

  return (
    <div className="space-y-5" data-testid="overview-summary">
      {/* 1. CHIFFRES CLÉS — 3 compteurs canoniques */}
      {(hasAnyData || visibleArticles.length > 0) && (
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-3 font-semibold">Chiffres clés</div>
          <div className="grid grid-cols-3 gap-2">
            <StatTile label="Médailles" value={medalsCount} testid="counter-medals" />
            <StatTile label="Médailles Or" value={goldCount} testid="counter-gold" />
            <StatTile label="Résultats enregistrés" value={resultsCount} testid="counter-results" />
          </div>
        </Card>
      )}

      {/* 2. TITRES MAJEURS — sync sur `is_major_title` du palmarès */}
      {topAchievements.length > 0 && (
        <Card className="p-5" data-testid="overview-highlights">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
            <h2 className="font-display font-semibold text-lg">Titres majeurs</h2>
            {explicitMajors.length > 0 && (
              <Badge variant="outline" className="text-[10px] ml-auto shrink-0">
                {explicitMajors.length} titre{explicitMajors.length > 1 ? "s" : ""} marqué{explicitMajors.length > 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          <div className="space-y-1.5">
            {topAchievements.map((a, i) => (
              <div key={a.id || i} className="text-sm flex items-start gap-2">
                {a.medal && (
                  <Badge className={`shrink-0 text-[9px] uppercase ${
                    a.medal === "or" ? "bg-[hsl(var(--aito-gold))] text-black"
                    : a.medal === "argent" ? "bg-slate-300 text-black"
                    : a.medal === "bronze" ? "bg-amber-700 text-white"
                    : "bg-secondary"
                  }`}>{a.medal}</Badge>
                )}
                <span className="min-w-0">
                  {a.year && <span className="text-muted-foreground mr-1.5">{a.year} —</span>}
                  <span className="font-medium">{a.title}</span>
                  {a.competition && a.competition !== a.title && <span className="text-muted-foreground"> · {a.competition}</span>}
                </span>
              </div>
            ))}
          </div>
          {explicitMajors.length === 0 && visibleAchievements.length > 0 && isOwner && (
            <p className="text-[11px] text-muted-foreground italic mt-3 border-t pt-2">
              💡 Astuce : coche « Titre majeur » sur tes résultats importants dans <Link to={`/athletes/${athlete.id}/edit`} className="underline text-[hsl(var(--aito-lagoon))]">Modifier ma fiche</Link> pour choisir précisément ce qui apparaît ici.
            </p>
          )}
        </Card>
      )}

      {/* 3. PALMARÈS & PARCOURS — directement dans l'aperçu, sous les titres majeurs */}
      {visibleAchievements.length > 0 && (
        <section data-testid="section-palmares-overview">
          <div className="flex items-end justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h2 className="font-display text-xl font-bold">Palmarès &amp; parcours</h2>
              <p className="text-xs text-muted-foreground">Une ligne par résultat officiel, groupés pour la lecture.</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Affichage :</span>
              <Button size="sm" variant={palmaresMode === "year" ? "default" : "outline"} onClick={() => setPalmaresMode("year")} data-testid="palmares-mode-year">Par année</Button>
              <Button size="sm" variant={palmaresMode === "discipline" ? "default" : "outline"} onClick={() => setPalmaresMode("discipline")} data-testid="palmares-mode-discipline">Par discipline</Button>
            </div>
          </div>
          {palmaresMode === "year"
            ? <PalmaresByYear achievements={visibleAchievements} disciplines={disciplines} athlete={athlete} />
            : <PalmaresByDiscipline achievements={visibleAchievements} disciplines={disciplines} athlete={athlete} />
          }
        </section>
      )}

      {/* PARCOURS SPORTIF — récit libre */}
      {(() => {
        const cleanedJourney = cleanJourneyForDisplay(athlete.journey_text);
        if (!cleanedJourney) return null;
        return (
          <Card className="p-5" data-testid="overview-journey">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
              <h2 className="font-display font-semibold text-lg">Parcours sportif</h2>
            </div>
            <p className="text-[15px] leading-relaxed whitespace-pre-line">{cleanedJourney}</p>
          </Card>
        );
      })()}

      {/* 4. STATISTIQUES PAR DISCIPLINE (simplifiées) */}
      <DisciplineStatsTable athlete={athlete} disciplines={disciplines} />

      {!hasAnyData && (
        <Card className="p-6 text-center border-dashed">
          <p className="text-sm text-muted-foreground">Cette fiche est en cours de construction.</p>
          {isOwner && (
            <Link to={`/athletes/${athlete.id}/edit`} className="inline-flex items-center gap-1 mt-2 text-sm text-[hsl(var(--aito-lagoon))] hover:underline">
              <Pencil className="h-3.5 w-3.5" />Compléter ma fiche
            </Link>
          )}
        </Card>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// ArticleMediaCard — Carte média avec miniature (cascade fallback) et CTA
// ---------------------------------------------------------------------------
const ARTICLE_KIND_LABELS = {
  article: "Article",
  interview: "Interview",
  portrait: "Portrait",
  video: "Vidéo",
  resultat: "Résultat",
  publication_institutionnelle: "Publication",
  reportage_photo: "Reportage photo",
  annonce: "Annonce",
  communique: "Communiqué",
  reportage: "Reportage",
  live: "Live",
  resume: "Résumé",
};

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    if (host.includes("youtu.be")) return u.pathname.replace(/^\//, "").split("/")[0] || null;
    if (host.includes("youtube.com")) {
      if (u.pathname.startsWith("/watch")) return u.searchParams.get("v");
      if (u.pathname.startsWith("/embed/")) return u.pathname.split("/embed/")[1]?.split("/")[0] || null;
      if (u.pathname.startsWith("/shorts/")) return u.pathname.split("/shorts/")[1]?.split("/")[0] || null;
    }
  } catch { /* ignore */ }
  return null;
}

function resolveArticleThumbnail(art) {
  // Cascade : manuel (image_url ou photo_url) → og_image stocké → YouTube auto → null
  if (art.image_url) return { src: art.image_url, isVideo: false };
  if (art.photo_url) return { src: art.photo_url, isVideo: false };
  if (art.og_image) return { src: art.og_image, isVideo: false };
  const ytId = extractYouTubeId(art.url);
  if (ytId) return { src: `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`, isVideo: true };
  return null;
}

// V9.7 — Cache module-level pour éviter de re-fetcher la même URL plusieurs fois
// dans la même session (ex. plusieurs articles sur la même fiche athlète).
const _ogScrapeCache = new Map();

async function fetchOgImage(url) {
  if (!url) return null;
  if (_ogScrapeCache.has(url)) return _ogScrapeCache.get(url);
  try {
    const r = await api.get("/og/scrape", { params: { url } });
    const img = r.data?.image_url || null;
    _ogScrapeCache.set(url, img);
    return img;
  } catch {
    _ogScrapeCache.set(url, null);
    return null;
  }
}

function ArticleMediaCard({ art, disciplines }) {
  const initialThumb = resolveArticleThumbnail(art);
  const [thumb, setThumb] = useState(initialThumb);
  const kindLabel = ARTICLE_KIND_LABELS[art.kind] || "Article";
  const isVideo = thumb?.isVideo || art.kind === "video";
  const disciplineName = art.discipline ? (disciplines.find((d) => d.slug === art.discipline)?.name || art.discipline) : null;

  // V9.7 — Lazy fetch OG image si aucune source d'image initiale et URL externe disponible.
  // Évite SSRF en s'appuyant sur l'endpoint backend `/api/og/scrape` (whitelist domaines publics).
  useEffect(() => {
    if (initialThumb || !art.url) return;
    let cancelled = false;
    fetchOgImage(art.url).then((img) => {
      if (!cancelled && img) setThumb({ src: img, isVideo: false });
    });
    return () => { cancelled = true; };
  }, [art.url, initialThumb]);
  return (
    <Card className="overflow-hidden flex flex-col aito-card-hover" data-testid="athlete-article">
      <a href={art.url} target="_blank" rel="noreferrer" className="relative block aspect-[16/9] bg-muted overflow-hidden group">
        {thumb ? (
          <img
            src={thumb.src}
            alt={art.title || ""}
            className="h-full w-full object-cover transition group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentNode.classList.add("flex", "items-center", "justify-center"); }}
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-muted-foreground">
            {isVideo ? <Play className="h-10 w-10" /> : <ImageIcon className="h-10 w-10" />}
          </div>
        )}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="rounded-full bg-black/60 p-3 backdrop-blur-sm">
              <Play className="h-6 w-6 text-white" fill="white" />
            </div>
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge className={`text-[10px] gap-1 border-0 ${isVideo ? "bg-[hsl(var(--aito-lagoon))] text-black" : "bg-[hsl(var(--aito-gold))] text-black"}`}>
            {isVideo && <Play className="h-3 w-3" fill="currentColor" />}
            {kindLabel}
          </Badge>
        </div>
      </a>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex flex-wrap items-center gap-1.5 mb-2 text-[11px] text-muted-foreground">
          {art.media && <span className="font-medium">{art.media}</span>}
          {art.date && (<><span>·</span><span>{art.date}</span></>)}
          {disciplineName && (<><span>·</span><span className="text-[hsl(var(--aito-lagoon))]">{disciplineName}</span></>)}
        </div>
        <a href={art.url} target="_blank" rel="noreferrer" className="font-display font-semibold text-base leading-snug hover:text-[hsl(var(--aito-lagoon))] line-clamp-2 mb-2">{art.title}</a>
        {(art.description || art.summary) && (
          <p className="text-xs text-muted-foreground line-clamp-3 mb-3">{art.description || art.summary}</p>
        )}
        <div className="mt-auto">
          <a href={art.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--aito-lagoon))] hover:underline" data-testid="article-read-link">
            {isVideo ? "Voir la vidéo" : "Lire l'article"} <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </Card>
  );
}


function PalmaresByYear({ achievements, disciplines, athlete }) {
  const { byYear, sortedYears } = useMemo(() => groupAchievementsByYearDiscipline(achievements), [achievements]);
  const disciplineName = (slug) => disciplines.find((d) => d.slug === slug)?.name || slug;
  if ((achievements || []).length === 0) return <p className="text-sm text-muted-foreground">Aucun palmarès renseigné. Ajoutes-en pour valoriser ta fiche.</p>;
  return (
    <div className="space-y-6" data-testid="palmares-by-year">
      {sortedYears.map((year) => (
        <section key={year}>
          <div className="flex items-center gap-3 mb-3">
            <span className="font-mono text-2xl font-bold text-[hsl(var(--aito-lagoon))]">{year}</span>
            <span className="flex-1 h-px bg-border" />
          </div>
          {Object.entries(byYear[year]).map(([disc, items]) => (
            <div key={disc} className="mb-4">
              <h4 className="font-display text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-2">{disciplineName(disc)}</h4>
              <div className="flex flex-col gap-2">
                {items.map((a, i) => <AchievementCard key={a.id || i} a={a} athlete={athlete} />)}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

function PalmaresByDiscipline({ achievements, disciplines, athlete }) {
  const byDisc = useMemo(() => {
    const m = {};
    for (const a of achievements || []) {
      const d = a.discipline || "Sans discipline";
      m[d] = m[d] || [];
      m[d].push(a);
    }
    return m;
  }, [achievements]);
  const disciplineName = (slug) => disciplines.find((d) => d.slug === slug)?.name || slug;
  return (
    <div className="space-y-5">
      {Object.entries(byDisc).map(([disc, items]) => (
        <section key={disc}>
          <h3 className="font-display font-semibold text-lg mb-2">{disciplineName(disc)} <span className="text-muted-foreground text-sm font-mono">({items.length})</span></h3>
          <div className="flex flex-col gap-2">
            {[...items].sort((a, b) => {
              // V9.12 — tri chronologique fin (année + mois) descendant
              const ka = (a.year || 0) * 100 + (a.month && Number.isFinite(+a.month) ? +a.month : 6);
              const kb = (b.year || 0) * 100 + (b.month && Number.isFinite(+b.month) ? +b.month : 6);
              return kb - ka;
            }).map((a, i) => <AchievementCard key={a.id || i} a={a} athlete={athlete} />)}
          </div>
        </section>
      ))}
    </div>
  );
}

// ---- Suggestion component (multi-destinations) ---------------------------
// Toutes les destinations possibles pour une suggestion IA (cf. cahier des charges).
const SUGGESTION_DESTINATIONS = [
  { id: "add_to_articles", label: "Ajouter dans Articles", icon: Newspaper, group: "rubriques" },
  { id: "add_to_media", label: "Ajouter la miniature dans Médias", icon: ImageIcon, group: "rubriques" },
  { id: "add_to_gallery", label: "Ajouter l'image dans Galerie", icon: Camera, group: "rubriques", requiresRights: true },
  { id: "add_to_sources", label: "Ajouter comme Source", icon: ExternalLink, group: "rubriques" },
  { id: "add_to_palmares", label: "Associer à un élément de Palmarès", icon: Trophy, group: "rubriques" },
  { id: "add_to_journey", label: "Associer au Parcours", icon: BookOpen, group: "rubriques" },
  { id: "link_coach", label: "Associer au coach cité", icon: ShieldCheck, group: "entites", requiresName: "coach" },
  { id: "link_club", label: "Associer au club cité", icon: Building2, group: "entites", requiresName: "club" },
  { id: "link_federation", label: "Associer à la fédération citée", icon: Crown, group: "entites", requiresName: "federation" },
  { id: "link_competition", label: "Associer à la compétition citée", icon: Calendar, group: "entites", requiresName: "competition" },
];

function SuggestionItem({ s, athlete, athleteId, onChange, isOwner, disciplines }) {
  // Pré-sélection intelligente selon le type détecté par l'IA
  const defaultActions = (() => {
    const t = (s.type || "").toLowerCase();
    if (t === "photo") return ["add_to_gallery"];
    if (t === "video") return ["add_to_articles"];
    if (t === "result" || t === "achievement") return ["add_to_palmares", "add_to_sources"];
    return ["add_to_articles", "add_to_sources"];
  })();

  const [actions, setActions] = useState(new Set(defaultActions));
  const [editMode, setEditMode] = useState(false);
  const [rights, setRights] = useState(false);
  const [targetAchievement, setTargetAchievement] = useState("");
  const [overrides, setOverrides] = useState({
    title: s.title || "",
    summary: s.summary || "",
    image_url: s.image_url || s.photo_url || "",
    source_url: s.source_url || "",
    media: s.media || s.source_title || "",
    date: s.date || "",
    year: s.year || "",
    discipline: s.discipline || "",
    kind: s.kind || (s.type === "video" ? "video" : "article"),
    source_type: s.source_type || "presse",
    coach: s.coach || s.coach_name || "",
    club: s.club || "",
    federation: s.federation || "",
    competition: s.competition || "",
  });

  const toggle = (id) => {
    setActions((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const upd = (patch) => setOverrides((c) => ({ ...c, ...patch }));

  const submit = async (mode) => {
    try {
      const body = { status: mode === "validate" ? "validated" : (mode === "ignore" ? "ignored" : "pending") };
      if (mode === "validate") {
        const arr = Array.from(actions);
        if (arr.length === 0) {
          toast.error("Coche au moins une destination avant de valider.");
          return;
        }
        body.actions = arr;
        // Overrides : ne pas envoyer les clés vides — on n'écrase pas par accident
        body.overrides = Object.fromEntries(Object.entries(overrides).filter(([_, v]) => v !== "" && v !== null && v !== undefined));
        if (arr.includes("add_to_gallery") || arr.includes("add_to_photos")) {
          if (!rights) { toast.error("Confirme les droits d'image avant d'ajouter dans la Galerie."); return; }
          body.rights_confirmed = true;
        }
        if (arr.includes("add_to_palmares") && targetAchievement !== "" && targetAchievement !== "new") {
          body.target_achievement_index = parseInt(targetAchievement);
        }
      }
      const { data } = await api.patch(`/athletes/${athleteId}/suggestions/${s.id}`, body);
      onChange(data);
      const applied = data._applied_actions || [];
      if (mode === "validate") toast.success(`Validée — ${applied.length} action${applied.length > 1 ? "s" : ""} appliquée${applied.length > 1 ? "s" : ""}.`);
      else if (mode === "ignore") toast.success("Suggestion ignorée.");
      setEditMode(false);
    } catch (e) {
      showError(e);
    }
  };

  const remove = async () => {
    if (!confirm("Supprimer définitivement cette suggestion ?")) return;
    try {
      const { data } = await api.delete(`/athletes/${athleteId}/suggestions/${s.id}`);
      onChange(data);
      toast.success("Suggestion supprimée.");
    } catch (e) { showError(e); }
  };

  const statusBadge = {
    pending: <Badge variant="secondary">À valider</Badge>,
    rights_check: <Badge className="bg-[hsl(var(--aito-energy))] text-black"><AlertTriangle className="h-3 w-3 mr-1" />Droits à vérifier</Badge>,
    validated: <Badge className="bg-[hsl(var(--success))] text-white">Validée</Badge>,
    ignored: <Badge variant="outline">Ignorée</Badge>,
    refused: <Badge variant="destructive">Refusée</Badge>,
  }[s.status || "pending"];

  const editable = isOwner && ["pending", "rights_check", "doubt"].includes(s.status || "pending");
  const detectedEntities = [
    overrides.coach && { kind: "Coach", name: overrides.coach },
    overrides.club && { kind: "Club", name: overrides.club },
    overrides.federation && { kind: "Fédération", name: overrides.federation },
    overrides.competition && { kind: "Compétition", name: overrides.competition },
  ].filter(Boolean);

  return (
    <Card className="overflow-hidden" data-testid="suggestion-item">
      <div className="flex flex-col sm:flex-row gap-3 p-4">
        {/* Miniature à gauche */}
        {(overrides.image_url || s.image_url) && (
          <a href={overrides.source_url || s.source_url || "#"} target="_blank" rel="noreferrer" className="sm:w-40 shrink-0 block">
            <img src={overrides.image_url || s.image_url} alt="" className="h-28 sm:h-full w-full object-cover rounded border" />
          </a>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <Badge variant="outline" className="capitalize text-[10px]">{overrides.kind || s.type || "article"}</Badge>
            {statusBadge}
            {overrides.discipline && <Badge variant="secondary" className="text-[10px]">{disciplines?.find((d) => d.slug === overrides.discipline)?.name || overrides.discipline}</Badge>}
            {overrides.year && <Badge variant="secondary" className="text-[10px]">{overrides.year}</Badge>}
            {s.confidence != null && <ConfidenceDot score={s.confidence} />}
          </div>
          <h4 className="font-display font-semibold leading-tight text-base">{overrides.title || s.title}</h4>
          {(overrides.summary || s.summary) && <p className="text-xs text-muted-foreground mt-1 line-clamp-3">{overrides.summary || s.summary}</p>}
          <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
            {(overrides.source_url || s.source_url) && <a href={overrides.source_url || s.source_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--aito-lagoon))] hover:underline inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Source</a>}
            {(overrides.media || s.source_title) && <span className="text-muted-foreground">· {overrides.media || s.source_title}</span>}
            {(overrides.date || s.date) && <span className="text-muted-foreground">· {overrides.date || s.date}</span>}
          </div>
          {detectedEntities.length > 0 && (
            <div className="mt-2 text-[11px] flex flex-wrap gap-1.5">
              <span className="text-muted-foreground uppercase tracking-wide">Entités détectées :</span>
              {detectedEntities.map((e, i) => <Badge key={i} variant="outline" className="text-[10px]">{e.kind} : {e.name}</Badge>)}
            </div>
          )}
        </div>
      </div>

      {/* Mode édition (modifier les champs avant validation) */}
      {editMode && editable && (
        <div className="border-t bg-secondary/30 p-4 space-y-2" data-testid="suggestion-edit-form">
          <div className="grid sm:grid-cols-2 gap-2 text-xs">
            <div><label className="block mb-0.5 text-muted-foreground">Titre</label><Input value={overrides.title} onChange={(e) => upd({ title: e.target.value })} /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Type</label>
              <select className="w-full border rounded-md px-2 py-1.5 bg-background" value={overrides.kind} onChange={(e) => upd({ kind: e.target.value })}>
                <option value="article">Article</option><option value="interview">Interview</option><option value="portrait">Portrait</option>
                <option value="video">Vidéo</option><option value="resultat">Résultat</option>
                <option value="publication_institutionnelle">Publication institutionnelle</option>
              </select>
            </div>
            <div><label className="block mb-0.5 text-muted-foreground">Média / Source</label><Input value={overrides.media} onChange={(e) => upd({ media: e.target.value })} /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Date</label><Input value={overrides.date} onChange={(e) => upd({ date: e.target.value })} /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Discipline</label>
              <select className="w-full border rounded-md px-2 py-1.5 bg-background" value={overrides.discipline} onChange={(e) => upd({ discipline: e.target.value })}>
                <option value="">—</option>
                {disciplines?.map((d) => <option key={d.slug} value={d.slug}>{d.name}</option>)}
              </select>
            </div>
            <div><label className="block mb-0.5 text-muted-foreground">Année</label><Input type="number" value={overrides.year} onChange={(e) => upd({ year: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="block mb-0.5 text-muted-foreground">Résumé</label><Textarea rows={2} value={overrides.summary} onChange={(e) => upd({ summary: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="block mb-0.5 text-muted-foreground">URL miniature</label><Input value={overrides.image_url} onChange={(e) => upd({ image_url: e.target.value })} /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Coach cité</label><Input value={overrides.coach} onChange={(e) => upd({ coach: e.target.value })} placeholder="Nom du coach" /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Club cité</label><Input value={overrides.club} onChange={(e) => upd({ club: e.target.value })} placeholder="Nom du club" /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Fédération citée</label><Input value={overrides.federation} onChange={(e) => upd({ federation: e.target.value })} placeholder="Nom de la fédération" /></div>
            <div><label className="block mb-0.5 text-muted-foreground">Compétition citée</label><Input value={overrides.competition} onChange={(e) => upd({ competition: e.target.value })} placeholder="Nom de la compétition" /></div>
          </div>
        </div>
      )}

      {/* Destinations multi-choix + actions */}
      {editable && (
        <div className="border-t bg-card p-4 space-y-3" data-testid="suggestion-destinations">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2">Destinations (coche une ou plusieurs)</div>
            <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1.5">
              {SUGGESTION_DESTINATIONS.filter((d) => d.group === "rubriques").map((dest) => (
                <label key={dest.id} className="flex items-center gap-2 text-xs cursor-pointer hover:text-foreground transition" data-testid={`sugg-dest-${dest.id}`}>
                  <Checkbox checked={actions.has(dest.id)} onCheckedChange={() => toggle(dest.id)} />
                  <dest.icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{dest.label}{dest.requiresRights && actions.has(dest.id) && !rights && <span className="text-[hsl(var(--aito-energy))] ml-1">— droits requis</span>}</span>
                </label>
              ))}
            </div>
            {/* Associations entités si renseignées */}
            {SUGGESTION_DESTINATIONS.filter((d) => d.group === "entites" && overrides[d.requiresName]).length > 0 && (
              <div className="mt-3 pt-3 border-t border-dashed">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1.5">Associations entités</div>
                <div className="grid sm:grid-cols-2 gap-x-3 gap-y-1.5">
                  {SUGGESTION_DESTINATIONS.filter((d) => d.group === "entites" && overrides[d.requiresName]).map((dest) => (
                    <label key={dest.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`sugg-dest-${dest.id}`}>
                      <Checkbox checked={actions.has(dest.id)} onCheckedChange={() => toggle(dest.id)} />
                      <dest.icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <span>{dest.label} : <span className="text-foreground font-medium">{overrides[dest.requiresName]}</span></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sélection cible si add_to_palmares */}
          {actions.has("add_to_palmares") && (athlete.achievements || []).length > 0 && (
            <div className="rounded-md border bg-secondary/40 p-2 text-xs space-y-1">
              <label className="block text-muted-foreground">Associer à un palmarès existant (ou créer nouveau) :</label>
              <select className="w-full border rounded px-2 py-1 bg-background" value={targetAchievement} onChange={(e) => setTargetAchievement(e.target.value)} data-testid="sugg-target-achievement">
                <option value="new">+ Créer un nouveau résultat</option>
                {athlete.achievements.map((a, i) => <option key={i} value={i}>{a.year || "—"} · {a.title}</option>)}
              </select>
            </div>
          )}

          {/* Confirmation droits si add_to_gallery */}
          {(actions.has("add_to_gallery") || actions.has("add_to_photos")) && (
            <label className="flex items-start gap-2 text-xs rounded-md border border-[hsl(var(--aito-energy))]/40 bg-[hsl(var(--aito-energy))]/5 p-2">
              <Checkbox checked={rights} onCheckedChange={(v) => setRights(!!v)} data-testid="sugg-rights-check" />
              <span>Je confirme disposer des droits nécessaires pour publier cette image sur AITO.PF.</span>
            </label>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button size="sm" onClick={() => submit("validate")} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="sugg-validate">
              <ShieldCheck className="h-3.5 w-3.5 mr-1" />Valider {actions.size > 0 && `(${actions.size})`}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditMode((v) => !v)} data-testid="sugg-edit"><Pencil className="h-3.5 w-3.5 mr-1" />{editMode ? "Masquer l'édition" : "Modifier"}</Button>
            <Button size="sm" variant="ghost" onClick={() => submit("ignore")} data-testid="sugg-ignore"><X className="h-3.5 w-3.5 mr-1" />Ignorer</Button>
            <Button size="sm" variant="ghost" onClick={remove} data-testid="sugg-remove" className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-auto"><Trash2 className="h-3.5 w-3.5 mr-1" />Supprimer</Button>
          </div>
        </div>
      )}

      {/* État final si déjà traitée */}
      {!editable && (s.applied_actions || []).length > 0 && (
        <div className="border-t bg-secondary/30 p-3 text-[11px] text-muted-foreground">
          Actions appliquées : {(s.applied_actions || []).join(", ")}
        </div>
      )}
    </Card>
  );
}

// ---- Main page ------------------------------------------------------------
export default function AthleteDetailPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = ["apercu", "coach", "media", "articles", "liens", "suggestions"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "apercu";
  const { user } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disciplines, setDisciplines] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanUrls, setScanUrls] = useState("");
  const [palmaresMode, setPalmaresMode] = useState("year"); // year | discipline
  const [affiliations, setAffiliations] = useState({ coaches: [], clubs: [], federations: [] });

  useEffect(() => {
    setLoading(true);
    api.get(`/athletes/${slug}`).then((r) => {
      const data = r.data;
      // V9.16 — Si le backend a résolu un ancien slug (redirection post-fusion),
      // on aligne l'URL du navigateur sur le slug principal actuel pour éviter
      // les partages d'anciens liens et pour le SEO.
      if (data?._redirected_from && data.slug && data.slug !== slug) {
        try { window.history.replaceState(null, "", `/athletes/${data.slug}`); } catch {}
      }
      setAthlete(data);
    }).catch(() => setAthlete(null)).finally(() => setLoading(false));
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || []));
  }, [slug]);

  // Une seule requête pour résoudre les miniatures coach/club/fédé (au lieu de 3+ lookup parallèles).
  useEffect(() => {
    if (!athlete?.id) return;
    api.get(`/athletes/${athlete.id}/affiliations-resolved`)
      .then((r) => setAffiliations(r.data || { coaches: [], clubs: [], federations: [] }))
      .catch(() => { /* fallback silencieux : on garde les noms bruts */ });
  }, [athlete?.id]);

  const isOwner = !!(user && athlete && athlete.owner_user_id === user.id);
  // V9.14 — Admin & COPF peuvent aussi éditer n'importe quelle fiche (droit de modération).
  const userRoles = new Set((user?.roles) || []);
  const isAdminOrCopf = userRoles.has("admin") || userRoles.has("copf");
  const canEdit = isOwner || isAdminOrCopf;
  const editButtonLabel = isOwner ? "Modifier ma fiche" : "Modifier la fiche";

  // V11.3 — Sélection d'articles à inclure dans le PDF export.
  const [pdfArticleDialog, setPdfArticleDialog] = useState(false);
  const [pdfSelectedArticles, setPdfSelectedArticles] = useState({});

  // V13.9 — Dialog pré-export : proposer la validation officielle du palmarès
  const [preExportDialog, setPreExportDialog] = useState(false);
  const [validationRequesting, setValidationRequesting] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  // V13.15 — Contexte validation (options COPF / Fédération dispo pour cette fiche)
  const [validationCtx, setValidationCtx] = useState(null); // {can_ask_copf, can_ask_federation, federation_options, current}
  const [validationDialog, setValidationDialog] = useState(null); // "copf" | "federation" | null

  useEffect(() => {
    if (!athlete?.id || !user) { setValidationCtx(null); return; }
    let cancelled = false;
    api.get(`/athletes/${athlete.id}/validation/context`)
      .then((r) => { if (!cancelled) setValidationCtx(r.data); })
      .catch(() => { if (!cancelled) setValidationCtx(null); });
    return () => { cancelled = true; };
  }, [athlete?.id, user?.id]);

  const copfValidatedPalmares = !!athlete?.copf_palmares_validated_at
    || (athlete?.profile_validation_status === "validated" && athlete?.profile_validation_by_role === "copf");
  const fedValidatedPalmares = !!athlete?.federation_palmares_validated_at
    || (athlete?.profile_validation_status === "validated" && athlete?.profile_validation_by_role === "federation");
  const hasAnyPalmaresValidation = copfValidatedPalmares || fedValidatedPalmares;
  const isValidationPending = athlete?.profile_validation_status === "pending";
  // V13.20 — Une incohérence signalée invalide toute validation antérieure et
  // débloque la possibilité pour l'athlète de refaire une demande.
  const isValidationInconsistency = athlete?.profile_validation_status === "inconsistency";
  const inconsistencyNotes = isValidationInconsistency ? (athlete?.profile_validation_notes || "") : "";
  const inconsistencyByRole = isValidationInconsistency ? (athlete?.profile_validation_by_role || "") : "";

  const requestPalmaresValidation = async (target /* "federation" | "copf" */) => {
    if (!isOwner) return;
    setValidationRequesting(true);
    try {
      await api.post(`/athletes/${athlete.id}/validation/request`, {
        target,
        message: validationMessage || undefined,
      });
      toast.success(
        target === "copf"
          ? "Demande envoyée au COPF. Vous recevrez une notification une fois validée."
          : "Demande envoyée à votre fédération. Vous recevrez une notification une fois validée."
      );
      // Refresh athlete + context pour afficher le statut pending
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/athletes/${athlete.id}`),
          api.get(`/athletes/${athlete.id}/validation/context`),
        ]);
        setAthlete(r1.data);
        setValidationCtx(r2.data);
      } catch { /* noop */ }
      setValidationDialog(null);
      setPreExportDialog(false);
      setValidationMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'envoyer la demande.");
    } finally {
      setValidationRequesting(false);
    }
  };

  const handleExportClick = () => {
    // V13.15 — Plus de dialog intrusif : la validation se demande depuis le
    // bouton dédié du menu latéral. On va directement à l'export.
    proceedToExport();
  };

  const proceedToExport = () => {
    if ((athlete?.articles || []).length > 0) {
      openPdfDialog();
    } else {
      downloadPdf();
    }
  };

  const downloadPdf = async (opts = {}) => {
    try {
      const params = {};
      if (opts.exclude_articles) params.exclude_articles = true;
      else if (opts.article_ids && opts.article_ids.length) params.include_article_ids = opts.article_ids.join(",");
      const res = await api.post(`/ai/sponsor-pdf/athlete/${athlete.id}`, {}, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.download = `AITO_PF_Fiche_Sportive_${athlete.slug}.pdf`; link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Fiche sportive PDF téléchargée.");
    } catch (e) { toast.error("Erreur PDF."); }
  };

  const openPdfDialog = () => {
    // Pré-sélectionne tous les articles disponibles.
    const preset = {};
    for (const a of (athlete?.articles || [])) {
      const k = a.id || a.url;
      if (k) preset[k] = true;
    }
    setPdfSelectedArticles(preset);
    setPdfArticleDialog(true);
  };
  const confirmPdfExport = () => {
    const ids = Object.entries(pdfSelectedArticles).filter(([, v]) => v).map(([k]) => k);
    const hasArticles = (athlete?.articles || []).length > 0;
    if (!hasArticles) return downloadPdf();
    if (ids.length === 0) return downloadPdf({ exclude_articles: true });
    if (ids.length === (athlete.articles || []).length) return downloadPdf();
    return downloadPdf({ article_ids: ids });
  };

  const runScan = async () => {
    if (!isOwner) return;
    const urls = scanUrls.split(/\n|,/).map((u) => u.trim()).filter(Boolean);
    setScanning(true);
    try {
      const { data } = await api.post(`/ai/athletes/${athlete.id}/scan-sources`, urls.length ? { urls } : {});
      toast.success(`${data.appended} suggestion(s) détectée(s).`);
      const r = await api.get(`/athletes/${athlete.id}`); setAthlete(r.data);
    } catch (e) { showError(e); }
    finally { setScanning(false); }
  };

  // V11.18 — OG tags dynamiques pour partage riche Facebook / WhatsApp / X
  useOgTags(athlete ? {
    title: `${athlete.full_name} — Fiche AITO.PF`,
    description: athlete.bio_short
      || `Palmarès et parcours sportif de ${athlete.full_name}${athlete.main_discipline ? ` (${athlete.main_discipline})` : ""} sur AITO.PF.`,
    image: athlete.photo_url || athlete.banner_url || "/branding/og-share.png",
    url: `/athletes/${athlete.slug || athlete.id}`,
    type: "profile",
    // V13.25 — Fiches mineurs importées OU opposition en cours → noindex
    noindex: (
      (athlete.is_minor && (athlete.origin_type === "imported_unclaimed" || athlete.origin_type === "legacy_review"))
      || athlete.opposition_status === "requested"
      || athlete.opposition_status === "granted"
      || athlete.visibility_status === "hidden"
    ),
  } : null);

  if (loading) return <div className="mx-auto max-w-5xl p-6 space-y-4"><Skeleton className="h-48 rounded-2xl" /><Skeleton className="h-12 w-1/3" /></div>;
  if (!athlete) return <div className="mx-auto max-w-3xl p-8">Athlète introuvable. <Link to="/athletes" className="text-[hsl(var(--aito-lagoon))]">Retour</Link></div>;

  const initials = (athlete.full_name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const pendingSugg = (athlete.suggestions || []).filter((s) => ["pending", "rights_check"].includes(s.status));
  const orderedDisciplines = (athlete.discipline_order && athlete.discipline_order.length) ? athlete.discipline_order : (athlete.disciplines || []);

  // Filtre visibilité (masqué = exclu de la fiche publique sauf si propriétaire ?
  // Conformément au cahier des charges : caché partout sur la fiche publique, même pour le propriétaire
  // Filtrage articles masqués
  const visibleAchievements = (athlete.achievements || []).filter((ach) => !ach.hidden);
  // V13.1 — Tri des articles du plus récent au plus ancien.
  // Ordre de priorité des champs de date : date > published_at_source > published_at > added_at > created_at
  // Les articles sans date connue passent en dernier.
  const _articleDateMs = (art) => {
    const raw = art?.date || art?.published_at_source || art?.published_at || art?.added_at || art?.created_at;
    if (!raw) return -Infinity;
    const t = Date.parse(raw);
    return Number.isFinite(t) ? t : -Infinity;
  };
  const visibleArticles = (athlete.articles || [])
    .filter((art) => !art.hidden)
    .slice()
    .sort((a, b) => _articleDateMs(b) - _articleDateMs(a));

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6 overflow-x-clip">
      {/* V13.25 — Bannière RGPD pour fiches non revendiquées / legacy */}
      {(athlete?.origin_type === "imported_unclaimed" || athlete?.origin_type === "legacy_review") && (
        <UnclaimedAthleteBanner athlete={athlete} />
      )}
      {/* HERO */}
      <Card className="overflow-hidden bg-[hsl(var(--aito-black))] text-white border-0">
        <div className="relative aspect-[16/8] md:aspect-[16/6]">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/20 via-[hsl(var(--aito-black))]/60 to-[hsl(var(--aito-black))]" />
          {/* V13.22 — Bannière athlète : `object-center` pour centrer verticalement
              la photo (au lieu de la caler en haut). Améliore le rendu sur la
              plupart des cadrages, notamment quand la photo est un portrait
              cadré au milieu. */}
          <img
            src={resolveAthleteBanner(athlete)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center opacity-60"
            onError={(e) => { e.currentTarget.src = "/branding/cover-athlete-default.png"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 flex items-end gap-3 sm:gap-4 min-w-0">
            <Avatar className="h-16 w-16 sm:h-24 sm:w-24 border-2 border-white/50 shrink-0">
              <AvatarImage src={resolveAthletePhoto(athlete)} />
              <AvatarFallback className="bg-[hsl(var(--aito-lagoon))] text-black font-display text-2xl">{initials}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-xl sm:text-3xl font-bold leading-tight break-words" data-testid="athlete-detail-name">{athlete.full_name}</h1>
              {athlete.nickname && <div className="text-white/70 text-sm sm:text-base truncate">« {athlete.nickname} »</div>}
              {/* V11.8 — Badge "Créé par IA" visible pour admin/COPF uniquement */}
              {canEdit && (athlete.creation_source === "curator_ai" || (athlete.tags || []).includes("created_by_ai_from_article")) && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-violet-500/90 text-white border border-violet-300"
                  data-testid="athlete-detail-ai-badge"
                  title="Fiche créée automatiquement par l'IA depuis un article du Curator. À enrichir en priorité."
                >
                  🤖 Créé par IA · à enrichir
                </div>
              )}
              {/* V13.6 — Badges "top_titles" retirés du hero. Les titres majeurs
                  se pilotent désormais via la case "Titre majeur" sur chaque
                  résultat du palmarès (visibles dans la carte Titres majeurs). */}
              <ProfileBadges athlete={athlete} />
            </div>
            {canEdit && (
              <div className="hidden sm:flex flex-col gap-2">
                <Link to={`/athletes/${athlete.id}/edit`}><Button size="sm" variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20" data-testid="athlete-edit-button"><Pencil className="h-4 w-4 mr-1.5" />{editButtonLabel}</Button></Link>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="mt-4 grid lg:grid-cols-12 gap-6 min-w-0">
        <div className="lg:col-span-8 space-y-4 min-w-0">
          {/* V9.16 — Sprint 3 Lot A.3 : Bloc "Cette fiche vous concerne ?" */}
          <AthleteClaimCTA athlete={athlete} />
          {/* V11.18 — Prompt "Ajoute ta photo" pour les fiches sans photo réelle.
              Une vraie photo est requise pour apparaître dans les "Athlètes à la UNE"
              (contrôle de complétude du scoring HERO). */}
          {canEdit && !athlete.photo_url && (
            <Card
              className="p-4 flex items-start gap-3 border-2 border-dashed border-[hsl(var(--aito-gold))] bg-[hsl(var(--aito-gold))]/5"
              data-testid="athlete-missing-photo-prompt"
            >
              <div className="h-12 w-12 rounded-full bg-[hsl(var(--aito-gold))]/20 flex items-center justify-center shrink-0">
                <Camera className="h-6 w-6 text-[hsl(var(--aito-gold))]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-sm">Ajoute ta photo officielle</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Une photo est nécessaire pour apparaître dans les <strong>Athlètes à la UNE</strong> sur l'accueil et pour un meilleur partage social. Ta silhouette temporaire s'affiche en attendant.
                </p>
                <Link to={`/athletes/${athlete.id}/edit`}>
                  <Button
                    size="sm"
                    className="mt-2 bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]/80"
                    data-testid="athlete-missing-photo-cta"
                  >
                    <Camera className="h-4 w-4 mr-1.5" /> Ajouter ma photo
                  </Button>
                </Link>
              </div>
            </Card>
          )}
          <SupportCampaignBanner beneficiaryType="athlete" beneficiaryId={athlete.id} />
          <AthleteCampaignBanner athleteId={athlete.id} />

          {/* Bio short */}
          {athlete.bio_short && (
            <Card className="p-5 border-l-4 border-[hsl(var(--aito-lagoon))]">
              <p className="text-[15px] leading-relaxed">{athlete.bio_short}</p>
            </Card>
          )}

          {/* RECAP ECLAIR */}
          <FlashRecapCard athlete={athlete} />

          {/* V13.6 — HighlightsCard retirée : les faits marquants sont désormais
              consolidés dans la section "Parcours sportif" (texte libre). */}

          {/* Tabs — V9.13 masquage des onglets vides pour visiteur public.
              Owner/admin voient toujours tous les onglets pour compléter la fiche. */}
          {(() => {
            const hasAchievements = (athlete.achievements || []).some((a) => !a.hidden);
            const hasMedia = (athlete.gallery || []).length > 0 || (athlete.videos || []).length > 0;
            const hasArticles = (athlete.articles || []).length > 0;
            const hasLinks = (athlete.clubs || []).length > 0 || (athlete.coaches || []).length > 0 || (athlete.federations || []).length > 0;
            const forceAll = !!canEdit; // Owner/admin voient toujours tous les onglets
            const show = {
              apercu: true,
              palmares: forceAll || hasAchievements,
              media: forceAll || hasMedia,
              articles: forceAll || hasArticles,
              liens: forceAll || hasLinks,
            };
            return (
          <Tabs defaultValue={initialTab}>
            <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
              <TabsTrigger value="apercu" data-testid="tab-apercu">Aperçu</TabsTrigger>
              {athlete.also_offers_coaching && (
                <TabsTrigger value="coach" data-testid="tab-coach">Coach</TabsTrigger>
              )}
              {show.media && <TabsTrigger value="media" data-testid="tab-media">Médias</TabsTrigger>}
              {show.articles && <TabsTrigger value="articles" data-testid="tab-articles">Articles &amp; sources</TabsTrigger>}
              {show.liens && <TabsTrigger value="liens" data-testid="tab-liens-sportifs">Liens sportifs</TabsTrigger>}
              {isOwner && <TabsTrigger value="suggestions" data-testid="tab-suggestions">Suggestions IA{pendingSugg.length ? ` (${pendingSugg.length})` : ""}</TabsTrigger>}
            </TabsList>

            <TabsContent value="apercu" className="mt-4">
              <OverviewSummary
                athlete={athlete}
                disciplines={disciplines}
                visibleAchievements={visibleAchievements}
                visibleArticles={visibleArticles}
                isOwner={canEdit}
                palmaresMode={palmaresMode}
                setPalmaresMode={setPalmaresMode}
              />
              {/* V13.6 — La section Palmarès+Parcours a été fusionnée dans OverviewSummary
                  (sous les Titres majeurs). Elle bénéficie ainsi de la même visibilité
                  qu'au premier chargement de la fiche. */}
              {(athlete.results || []).length > 0 && (
                <div className="mt-6">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">Compétitions et classements complémentaires</div>
                  <Card className="overflow-hidden"><div className="divide-y">{athlete.results.map((r, i) => (
                    <div key={i} className="flex items-center justify-between p-3">
                      <div className="min-w-0"><div className="font-display text-sm font-semibold truncate">{r.competition}</div><div className="text-xs text-muted-foreground">{[r.date, r.category, r.discipline].filter(Boolean).join(" · ")}</div></div>
                      <div className="font-mono text-sm">{r.result || (r.rank ? `#${r.rank}` : "—")}</div>
                    </div>
                  ))}</div></Card>
                </div>
              )}
            </TabsContent>

            {athlete.also_offers_coaching && (
              <TabsContent value="coach" className="mt-4">
                <CoachPanel slug={athlete.slug || athlete.id} />
              </TabsContent>
            )}

            <TabsContent value="media" className="mt-4 space-y-4">
              <div>
                <h3 className="font-display font-semibold mb-2">Photos</h3>
                {(athlete.photos || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune photo.</p> : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {athlete.photos.map((p, i) => <img key={p.id || i} src={p.url} alt={p.caption || ""} className="aspect-square w-full object-cover rounded-lg border" data-testid="athlete-photo" />)}
                  </div>
                )}
              </div>
              <div>
                <h3 className="font-display font-semibold mb-2">Vidéos</h3>
                {(athlete.videos || []).length === 0 ? <p className="text-sm text-muted-foreground">Aucune vidéo.</p> : (
                  <ul className="space-y-2">{athlete.videos.map((v, i) => (
                    <li key={v.id || i}><a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted" data-testid="athlete-video"><Radio className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /><span className="flex-1 truncate text-sm">{v.title || v.url}</span><Badge variant="secondary" className="capitalize">{v.platform || "lien"}</Badge></a></li>
                  ))}</ul>
                )}
              </div>
              {(athlete.archives || []).length > 0 && (
                <div>
                  <h3 className="font-display font-semibold mb-2">Archives & documents</h3>
                  <ul className="space-y-2">{athlete.archives.map((a, i) => (
                    <li key={a.id || i}><a href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted" data-testid="athlete-archive">
                      <FileDown className="h-4 w-4 text-[hsl(var(--aito-gold))]" /><span className="flex-1 truncate text-sm">{a.caption || a.url}</span>
                      {a.year && <Badge variant="secondary">{a.year}</Badge>}
                    </a></li>
                  ))}</ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="articles" className="mt-4 space-y-5">
              {/* Articles */}
              <section data-testid="section-articles">
                <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2">Articles &amp; médias</h3>
                {visibleArticles.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun article référencé.</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="articles-grid">
                    {visibleArticles.map((art, i) => <ArticleMediaCard key={art.id || i} art={art} disciplines={disciplines} />)}
                  </div>
                )}
              </section>
              {/* V11.15 — Les réseaux sociaux sont maintenant dans la sidebar (toujours visibles) */}
              {/* Sources */}
              {(athlete.sources || []).length > 0 && (
                <section data-testid="section-sources">
                  <h3 className="font-display font-semibold text-sm uppercase tracking-wide text-muted-foreground mb-2 pt-2 border-t">Sources externes</h3>
                  <div className="space-y-2">
                    {athlete.sources.map((s, i) => (
                      <a key={i} href={s.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted text-sm">
                        <ExternalLink className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" /> <span className="flex-1 truncate">{s.title || s.url}</span>
                      </a>
                    ))}
                  </div>
                </section>
              )}
            </TabsContent>

            <TabsContent value="liens" className="mt-4 space-y-3" data-testid="tabcontent-liens-sportifs">
              <section>
                <div className="mb-3">
                  <h2 className="font-display text-xl font-bold">Liens sportifs</h2>
                  <p className="text-xs text-muted-foreground">Coachs, clubs, fédérations et autres entités liées à {athlete.full_name}.</p>
                </div>
                {(() => {
                  const aff = athlete.affiliations_resolved || athlete._affiliations_resolved || {};
                  const groups = [
                    { key: "coaches", label: "Coachs", path: "coachs", icon: "🥋", items: aff.coaches || [] },
                    { key: "clubs", label: "Clubs", path: "clubs", icon: "🏟️", items: aff.clubs || [] },
                    { key: "federations", label: "Fédérations", path: "federations", icon: "🏛️", items: aff.federations || [] },
                  ];
                  const totalLinks = groups.reduce((sum, g) => sum + g.items.length, 0);
                  if (totalLinks === 0) {
                    return <p className="text-sm text-muted-foreground">Aucun lien sportif déclaré pour le moment.</p>;
                  }
                  return groups.map((g) => g.items.length === 0 ? null : (
                    <div key={g.key} className="mb-5">
                      <h3 className="font-display text-sm font-semibold mb-2 flex items-center gap-2">
                        <span aria-hidden>{g.icon}</span> {g.label}
                        <Badge variant="outline" className="text-[10px]">{g.items.length}</Badge>
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {g.items.map((it) => (
                          <Link key={it.id} to={`/${g.path}/${it.slug || it.id}`} className="block" data-testid={`linked-${g.key}-${it.id}`}>
                            <Card className="p-3 hover:border-[hsl(var(--aito-lagoon))] transition aito-card-hover">
                              <div className="flex items-center gap-3 min-w-0">
                                {it.photo_url || it.logo_url ? (
                                  <img src={it.photo_url || it.logo_url} alt="" className="h-10 w-10 rounded-full object-cover shrink-0" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-[hsl(var(--aito-lagoon))]/10 flex items-center justify-center text-[hsl(var(--aito-lagoon))] font-semibold shrink-0">
                                    {(it.full_name || it.name || "?").slice(0, 1).toUpperCase()}
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="font-display font-semibold text-sm truncate">{it.full_name || it.name}</div>
                                  {it.kind === "athlete_as_coach" && <span className="text-[10px] text-[hsl(var(--aito-gold))]">★ Athlète aussi coach</span>}
                                  {it.role && <div className="text-[11px] text-muted-foreground truncate">{it.role}</div>}
                                </div>
                              </div>
                            </Card>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </section>

              {/* V13 — Partenaires (annuaire étendu) */}
              <section className="mt-6" data-testid="section-partners">
                <EntityRelationsWidget
                  entityType="athlete"
                  entityId={athlete.id}
                  entityName={athlete.full_name}
                  entityOwnerId={athlete.owner_user_id}
                  title="Partenaires (boutiques, santé, prestataires)"
                />
              </section>
            </TabsContent>

            {isOwner && (
              <TabsContent value="suggestions" className="mt-4 space-y-4">
                <Card className="p-4">
                  <h3 className="font-display font-semibold mb-2">Scanner des sources</h3>
                  <p className="text-xs text-muted-foreground mb-3">Collez des URLs (une par ligne). L'IA détecte articles, résultats, photos, vidéos.</p>
                  <textarea data-testid="scan-urls-input" className="w-full border rounded-lg p-2 text-sm font-mono" rows={3} placeholder={"https://...\nhttps://..."} value={scanUrls} onChange={(e) => setScanUrls(e.target.value)} />
                  <div className="flex gap-2 mt-2">
                    <Button onClick={runScan} disabled={scanning} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="scan-sources-run"><SearchIcon className="h-4 w-4 mr-1.5" />{scanning ? "Scan en cours…" : "Scanner"}</Button>
                    <Link to={`/athletes/${athlete.id}/edit?tab=monitor`}><Button variant="outline" size="sm">Gérer liens surveillés</Button></Link>
                  </div>
                </Card>
                {pendingSugg.length === 0 ? <p className="text-sm text-muted-foreground">Aucune suggestion en attente. Lancez un scan pour en générer.</p> :
                  <div className="space-y-2">{pendingSugg.map((s) => <SuggestionItem key={s.id} s={s} athlete={athlete} athleteId={athlete.id} onChange={setAthlete} isOwner={isOwner} disciplines={disciplines} />)}</div>
                }
              </TabsContent>
            )}
          </Tabs>
            );
          })()}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-4 space-y-3 min-w-0">
          <Card className="p-4">
            {isOwner && <Link to={`/athletes/${athlete.id}/edit`}><Button data-testid="athlete-detail-edit-button" className="w-full mb-2 bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]"><Pencil className="h-4 w-4 mr-2" />Modifier ma fiche</Button></Link>}
            <Button data-testid="athlete-follow-button" className="w-full mb-2" variant={isOwner ? "outline" : "default"}><Heart className="h-4 w-4 mr-2" />Suivre</Button>
            <Button data-testid="athlete-sponsor-pdf-button" onClick={handleExportClick} variant="outline" className="w-full border-[hsl(var(--aito-gold))] text-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold))] hover:text-black">
              <FileDown className="h-4 w-4 mr-2" />Exporter ma fiche sportive en PDF
            </Button>
            {/* V13.15 — Bloc Validation officielle du palmarès (owner uniquement) */}
            {isOwner && validationCtx && (
              <div className="mt-3 p-2.5 rounded-md border border-[hsl(var(--aito-gold))/40] bg-[hsl(var(--aito-gold))/5]" data-testid="palmares-validation-block">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--aito-gold))] font-bold mb-1.5 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" />Validation officielle du palmarès
                </div>
                {(validationCtx.current?.copf_validated || validationCtx.current?.federation_validated) && !isValidationInconsistency ? (
                  <div className="space-y-1">
                    {validationCtx.current.copf_validated && (
                      <div className="text-[11px] text-emerald-700 flex items-center gap-1" data-testid="validation-status-copf">
                        <CheckCircle2 className="h-3 w-3" /><span>Validé par le COPF</span>
                      </div>
                    )}
                    {validationCtx.current.federation_validated && (
                      <div className="text-[11px] text-teal-700 flex items-center gap-1" data-testid="validation-status-federation">
                        <CheckCircle2 className="h-3 w-3" /><span>Validé par {validationCtx.current.federation_name || "la Fédération"}</span>
                      </div>
                    )}
                  </div>
                ) : isValidationInconsistency ? (
                  <div
                    className="rounded-md border border-orange-300 bg-orange-50 p-2 mb-1.5"
                    data-testid="validation-status-inconsistency"
                  >
                    <div className="flex items-start gap-1.5 text-[11px] text-orange-800">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold">
                          Incohérence signalée par {inconsistencyByRole === "copf" ? "le COPF" : inconsistencyByRole === "federation" ? "votre fédération" : "un administrateur"}
                        </div>
                        {inconsistencyNotes && (
                          <div className="mt-0.5 text-[10.5px] leading-snug italic break-words" data-testid="validation-inconsistency-notes">
                            « {inconsistencyNotes} »
                          </div>
                        )}
                        <div className="mt-1 text-[10px] text-orange-700">
                          Corrigez les éléments concernés, puis renvoyez une demande.
                        </div>
                      </div>
                    </div>
                  </div>
                ) : isValidationPending ? (
                  <div className="text-[11px] text-amber-700 italic flex items-center gap-1" data-testid="validation-status-pending">
                    <Loader2 className="h-3 w-3 animate-spin" /><span>Demande en attente de traitement</span>
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground mb-2">Aucune validation officielle. Faites valider votre palmarès pour obtenir un sceau premium visible sur votre fiche et vos PDF.</p>
                )}
                {(!validationCtx.current?.copf_validated || isValidationInconsistency) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-1.5 h-7 text-[11px] border-[hsl(var(--aito-gold))] text-[hsl(var(--aito-gold))] hover:bg-[hsl(var(--aito-gold))] hover:text-black"
                    onClick={() => setValidationDialog("copf")}
                    data-testid="request-validation-copf"
                    disabled={isValidationPending}
                  >
                    <Crown className="h-3 w-3 mr-1" />
                    {isValidationInconsistency ? "Corriger et redemander au COPF" : "Demander validation au COPF"}
                  </Button>
                )}
                {validationCtx.can_ask_federation && (!validationCtx.current?.federation_validated || isValidationInconsistency) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-1.5 h-7 text-[11px] border-teal-600 text-teal-700 hover:bg-teal-600 hover:text-white"
                    onClick={() => setValidationDialog("federation")}
                    data-testid="request-validation-federation"
                    disabled={isValidationPending}
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    {isValidationInconsistency ? "Corriger et redemander à ma fédération" : "Demander à ma fédération"}
                  </Button>
                )}
                {!validationCtx.can_ask_federation && !validationCtx.current?.federation_validated && (
                  <p className="text-[10px] italic text-muted-foreground mt-1.5">
                    Fédération d'affiliation non recensée sur AITO.PF — seule la validation COPF est possible.
                  </p>
                )}
              </div>
            )}
            <div className="mt-2">
              <ShareButton
                kind="athlete"
                slug={athlete.slug || athlete.id}
                title={`${athlete.full_name} — Fiche sportive AITO.PF`}
                text={`Découvrez la fiche sportive de ${athlete.full_name} sur AITO.PF — palmarès, parcours et sources.`}
                canonicalPath={`/athletes/${athlete.slug || athlete.id}`}
                dataTestId="athlete-share-button"
                variant="ghost"
                size="default"
              />
              {/* V11.20 — Rafraîchir le cache Facebook (utile après modif photo/bio) */}
              {canEdit && (
                <a
                  href={`https://developers.facebook.com/tools/debug/?q=${encodeURIComponent(
                    (typeof window !== "undefined" ? window.location.origin : "") +
                      `/athletes/${athlete.slug || athlete.id}`,
                  )}`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="block mt-2 text-[11px] text-muted-foreground hover:text-[#1877F2] inline-flex items-center gap-1"
                  data-testid="athlete-refresh-facebook-cache"
                  title="Force Facebook à re-scraper l'aperçu (utile après changement de photo ou bio)"
                >
                  <Facebook className="h-3 w-3" /> Rafraîchir l'aperçu Facebook
                </a>
              )}
            </div>
          </Card>

          {/* V11.15 — Réseaux sociaux publics de l'athlète */}
          {(athlete.social_facebook || athlete.social_instagram || athlete.social_tiktok) && (
            <Card className="p-4" data-testid="sidebar-socials">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold">Réseaux sociaux</div>
              <div className="space-y-1.5">
                {athlete.social_facebook && (
                  <a
                    href={athlete.social_facebook}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-[#1877F2]/10 text-[#1877F2] border-[#1877F2]/30 hover:bg-[#1877F2] hover:text-white transition-colors text-sm font-medium"
                    data-testid="athlete-social-facebook"
                    title="Voir sur Facebook"
                  >
                    <Facebook className="h-4 w-4 shrink-0" />
                    <span className="truncate">Facebook</span>
                    <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-70 shrink-0" />
                  </a>
                )}
                {athlete.social_instagram && (
                  <a
                    href={athlete.social_instagram}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-pink-500/10 text-pink-600 border-pink-500/30 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 hover:text-white transition-colors text-sm font-medium"
                    data-testid="athlete-social-instagram"
                    title="Voir sur Instagram"
                  >
                    <Instagram className="h-4 w-4 shrink-0" />
                    <span className="truncate">Instagram</span>
                    <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-70 shrink-0" />
                  </a>
                )}
                {athlete.social_tiktok && (
                  <a
                    href={athlete.social_tiktok}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-foreground/5 text-foreground border-foreground/20 hover:bg-foreground hover:text-background transition-colors text-sm font-medium"
                    data-testid="athlete-social-tiktok"
                    title="Voir sur TikTok"
                  >
                    <TiktokIcon className="h-4 w-4 shrink-0" />
                    <span className="truncate">TikTok</span>
                    <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-70 shrink-0" />
                  </a>
                )}
              </div>
            </Card>
          )}

          {/* V12.x — Autres liens/profils externes (fédération internationale, Wikipedia, etc.) */}
          {Array.isArray(athlete.external_profiles) && athlete.external_profiles.length > 0 && (
            <Card className="p-4" data-testid="sidebar-external-profiles">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2 font-semibold flex items-center gap-1.5">
                <Link2 className="h-3.5 w-3.5" /> Autres liens & profils
              </div>
              <div className="space-y-1.5">
                {athlete.external_profiles.map((it, i) => (
                  <a
                    key={i}
                    href={it.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-[hsl(var(--aito-lagoon))]/8 text-[hsl(var(--aito-lagoon))] border-[hsl(var(--aito-lagoon))]/30 hover:bg-[hsl(var(--aito-lagoon))] hover:text-white transition-colors text-sm font-medium"
                    data-testid={`athlete-external-profile-${i}`}
                    title={it.url}
                  >
                    <Link2 className="h-4 w-4 shrink-0" />
                    <span className="truncate">{it.label || "Voir le profil"}</span>
                    <ArrowUpRight className="h-3.5 w-3.5 ml-auto opacity-70 shrink-0" />
                  </a>
                ))}
              </div>
            </Card>
          )}

          {/* V11.19 — Aperçu embarqué des derniers posts (Instagram / TikTok).
              Visible pour tout le monde si l'athlète a fourni au moins une URL.
              Si l'utilisateur peut éditer et qu'aucun post n'est renseigné, on
              affiche un prompt pour l'inciter à en ajouter un. */}
          {(() => {
            const igUrl = athlete.latest_instagram_post_url;
            const ttUrl = athlete.latest_tiktok_post_url;
            const igValid = !!igUrl && detectSocialPlatform(igUrl) === "instagram";
            const ttValid = !!ttUrl && detectSocialPlatform(ttUrl) === "tiktok";
            const hasAny = igValid || ttValid;

            if (!hasAny && !canEdit) return null;

            return (
              <Card className="p-4 space-y-3" data-testid="sidebar-social-embeds">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                    Derniers posts
                  </div>
                  {canEdit && hasAny && (
                    <Link
                      to={`/athletes/${athlete.id}/edit`}
                      className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      data-testid="social-embeds-edit-link"
                    >
                      <Pencil className="h-3 w-3" /> Modifier
                    </Link>
                  )}
                </div>

                {igValid && (
                  <SocialPostEmbed url={igUrl} testId="athlete-embed-instagram" />
                )}
                {ttValid && (
                  <SocialPostEmbed url={ttUrl} testId="athlete-embed-tiktok" />
                )}

                {/* Prompt admin / coach / owner : uniquement si aucun post valide */}
                {canEdit && !hasAny && (
                  <div
                    className="rounded-lg border-2 border-dashed border-[hsl(var(--aito-gold))]/60 bg-[hsl(var(--aito-gold))]/5 p-3 flex items-start gap-3"
                    data-testid="social-embeds-empty-prompt"
                  >
                    <div className="h-9 w-9 rounded-full bg-[hsl(var(--aito-gold))]/20 flex items-center justify-center shrink-0">
                      <Sparkles className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm">
                        Mets en avant un post
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                        Colle une URL Instagram ou TikTok pour afficher un aperçu embarqué directement ici.
                      </p>
                      <Link to={`/athletes/${athlete.id}/edit`}>
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 text-xs"
                          data-testid="social-embeds-empty-cta"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Ajouter un post
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </Card>
            );
          })()}
          {/* V13.6 — Bloc "Statut sportif proposé" retiré : remplacé par le badge officiel
              "Haut niveau confirmé" affiché dans la carte Récap éclair (visible plus haut). */}
          {(athlete.clubs?.length > 0 || athlete.federations?.length > 0 || athlete.coaches?.length > 0 || (athlete.badges_suggested || []).length > 0) && (
            <Card className="p-4 space-y-3" data-testid="sidebar-affiliations">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Coachs, clubs &amp; organisations</div>
              {athlete.coaches?.length > 0 && (
                <SidebarEntityList
                  icon={ShieldCheck}
                  iconColor="hsl(var(--aito-lagoon))"
                  label="Coach·s"
                  names={athlete.coaches}
                  kind="coach"
                  resolved={affiliations.coaches}
                />
              )}
              {athlete.clubs?.length > 0 && (
                <SidebarEntityList
                  icon={Building2}
                  iconColor="hsl(var(--aito-lagoon))"
                  label="Club·s"
                  names={athlete.clubs}
                  kind="club"
                  resolved={affiliations.clubs}
                />
              )}
              {athlete.federations?.length > 0 && (
                <SidebarEntityList
                  icon={Crown}
                  iconColor="hsl(var(--aito-gold))"
                  label="Fédération·s"
                  names={athlete.federations}
                  kind="federation"
                  resolved={affiliations.federations}
                />
              )}
              {(athlete.badges_suggested || []).length > 0 && (
                <div className="pt-2 border-t" data-testid="sidebar-distinctions">
                  <div className="text-xs text-muted-foreground mb-1.5">Distinctions</div>
                  <div className="flex flex-wrap gap-1.5">
                    {athlete.badges_suggested.map((b) => (
                      <Badge key={b} className="bg-black text-white border border-[hsl(var(--aito-gold))]">
                        <Crown className="h-3 w-3 mr-1" />{b.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
          {/* Partenaires officiels — mis en avant avec logos */}
          {((athlete.sponsor_partners || []).length > 0 || (athlete.sponsors || []).length > 0) && (
            <Card className="p-4 space-y-3 border-2 border-[hsl(var(--aito-gold)/0.4)] bg-gradient-to-br from-[hsl(var(--aito-gold)/0.04)] to-transparent" data-testid="sidebar-partners">
              <div className="flex items-center gap-1.5">
                <Heart className="h-4 w-4 text-[hsl(var(--aito-gold))] fill-[hsl(var(--aito-gold))]" />
                <span className="text-xs uppercase tracking-wide font-semibold text-[hsl(var(--aito-gold))]">Partenaires officiels</span>
              </div>
              {/* Sponsors riches (avec logos) */}
              {(athlete.sponsor_partners || []).length > 0 && (
                <div className="grid grid-cols-2 gap-2" data-testid="sidebar-partners-grid">
                  {(athlete.sponsor_partners || []).map((p, idx) => {
                    const tierTone = p.tier === "gold" ? "border-[hsl(var(--aito-gold))]"
                      : p.tier === "silver" ? "border-slate-400"
                      : p.tier === "bronze" ? "border-amber-700"
                      : "border-border";
                    const Inner = (
                      <div className={`aspect-square rounded-lg border-2 ${tierTone} bg-background flex flex-col items-center justify-center p-2 hover:bg-muted/50 transition-colors group relative overflow-hidden`} data-testid={`partner-card-${idx}`}>
                        {p.logo_url ? (
                          <img src={p.logo_url} alt={p.name} className="max-h-12 max-w-full object-contain" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                            <Heart className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="text-[10px] font-medium text-center mt-1.5 leading-tight line-clamp-2">{p.name}</div>
                        {p.tier && p.tier !== "standard" && (
                          <Badge variant="outline" className="absolute top-1 right-1 text-[8px] px-1 py-0 h-3.5 capitalize">{p.tier}</Badge>
                        )}
                      </div>
                    );
                    return p.url ? (
                      <a key={idx} href={p.url} target="_blank" rel="noreferrer" aria-label={`Site de ${p.name}`}>{Inner}</a>
                    ) : (
                      <div key={idx}>{Inner}</div>
                    );
                  })}
                </div>
              )}
              {/* Sponsors texte libre (legacy) */}
              {(athlete.sponsors || []).filter((s) => !(athlete.sponsor_partners || []).some((p) => p.name === s)).length > 0 && (
                <div className="space-y-1 pt-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Soutiens additionnels</div>
                  <div className="flex flex-wrap gap-1">
                    {(athlete.sponsors || []).filter((s) => !(athlete.sponsor_partners || []).some((p) => p.name === s)).map((s) => (
                      <Badge key={s} variant="outline" className="text-[11px]" data-testid={`partner-legacy-${s}`}>{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
          {(athlete.badges_suggested || []).length > 0 && (athlete.clubs?.length === 0 && athlete.federations?.length === 0 && athlete.coaches?.length === 0) && (
            /* Carte Distinctions seule si pas d'affiliations (sinon fusionnée dans Affiliations) */
            <Card className="p-4" data-testid="sidebar-distinctions-only">
              <div className="text-xs text-muted-foreground mb-2">Distinctions</div>
              <div className="flex flex-wrap gap-1.5">{athlete.badges_suggested.map((b) => <Badge key={b} className="bg-black text-white border border-[hsl(var(--aito-gold))]"><Crown className="h-3 w-3 mr-1" />{b.replace(/_/g, " ")}</Badge>)}</div>
            </Card>
          )}
        </aside>
      </div>

      {/* V13.15 — Dialog demande validation (COPF ou Fédération) — clair et ciblé */}
      <Dialog open={!!validationDialog} onOpenChange={(open) => { if (!open) { setValidationDialog(null); setValidationMessage(""); } }}>
        <DialogContent className="max-w-lg" data-testid="validation-request-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              {validationDialog === "copf" ? (
                <><Crown className="h-5 w-5 text-[hsl(var(--aito-gold))]" />Demander validation au COPF</>
              ) : (
                <><ShieldCheck className="h-5 w-5 text-teal-600" />Demander validation à ma fédération</>
              )}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              {validationDialog === "copf"
                ? "Le Comité Olympique Polynésien Français (COPF) vérifiera votre palmarès. Une fois validé, un sceau premium « Palmarès validé par le COPF » apparaîtra sur votre fiche et vos exports PDF."
                : (
                  <>
                    Votre fédération d'affiliation vérifiera votre palmarès.
                    {validationCtx?.federation_options?.length > 0 && (
                      <> Fédération identifiée : <strong>{validationCtx.federation_options.map(f => f.name).join(", ")}</strong>.</>
                    )}
                    {" "}Une fois validé, un sceau premium apparaîtra sur votre fiche et vos exports PDF.
                  </>
                )
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Message pour le validateur (optionnel)</label>
              <Textarea
                rows={3}
                value={validationMessage}
                onChange={(e) => setValidationMessage(e.target.value)}
                placeholder="Ex. : Mon palmarès est à jour, merci de confirmer les résultats de la saison 2027."
                data-testid="validation-request-message"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="ghost"
              onClick={() => { setValidationDialog(null); setValidationMessage(""); }}
              disabled={validationRequesting}
              data-testid="validation-request-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={() => requestPalmaresValidation(validationDialog)}
              disabled={validationRequesting}
              className={validationDialog === "copf"
                ? "bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]"
                : "bg-teal-600 text-white hover:bg-teal-700"}
              data-testid="validation-request-send"
            >
              {validationRequesting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V11.3 — Dialogue de sélection des articles à inclure dans le PDF */}
      <Dialog open={pdfArticleDialog} onOpenChange={setPdfArticleDialog}>
        <DialogContent className="max-w-md" data-testid="pdf-articles-dialog">
          <DialogHeader>
            <DialogTitle>Inclure les articles associés</DialogTitle>
            <DialogDescription>
              Sélectionnez les articles à intégrer à la fiche PDF. Les articles externes sans droit de reproduction n'apparaissent que sous forme de résumé et de lien vers la source (aucune copie intégrale automatique).
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-2 py-2">
            {(athlete?.articles || []).map((a) => {
              const k = a.id || a.url;
              return (
                <label key={k} className="flex items-start gap-2 text-sm cursor-pointer p-2 rounded hover:bg-muted/50" data-testid={`pdf-article-choice-${k}`}>
                  <Checkbox
                    checked={!!pdfSelectedArticles[k]}
                    onCheckedChange={(v) => setPdfSelectedArticles((s) => ({ ...s, [k]: !!v }))}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{a.title || a.url}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {[a.media, a.date].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPdfArticleDialog(false)} data-testid="pdf-dialog-cancel">Annuler</Button>
            <Button onClick={() => { setPdfArticleDialog(false); confirmPdfExport(); }} className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(43_74%_45%)]" data-testid="pdf-dialog-export">
              <FileDown className="h-4 w-4 mr-1.5" />Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
